import React from 'react'
import { readEndpoint } from 'redux-json-api'
import _ from 'lodash'
import { EmeraldHost, ApplicationHost } from './config'
import AuthenticationNotice from './components/AuthenticationNotice'

// General wrapper for executing both queries and mutations against the data graph.
// The onComplete callback needs to check for errors.
export const runQuery = (query, variables, callbacks) => {
  const fetchConfig = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  }
  fetch('/graphql', fetchConfig)
    .then(response => response.json())
    .then(json =>     callbacks.onComplete(json))
    .catch(error =>   callbacks.onFailure(error))
}

// helper function to do the following: will take the endpoint that we would like to make a request to, 
// and with readEndpoint(endpoint) it will append the endpoint to the root path to construct the full 
// request endpoint. Then on dispatch(), will read the data in the response object (if response is successful) 
// and map the data to state in redux store. On failure, with no Auth permission, will redirect user to login.
// Otherwise would simply print the error in console log.
export const apiReadWithAuth = (endpoint, callbacks = {}) => {
  const { onBegin, onSuccess, onError, onNeedAuth } = callbacks
  return dispatch => {
    if(_.isFunction(onBegin)){ onBegin(endpoint) }

    dispatch(readEndpoint(endpoint))
      .then(response=>{
        if(_.isFunction(onSuccess)){ onSuccess(response.body) }
      })
      .catch(error => {
        if(error.message.match(/code 401/i)) {
          if(_.isFunction(onNeedAuth)){ onNeedAuth() }
          window.location = EmeraldHost()+`login?service=${encodeURIComponent(ApplicationHost()+'users/service')}`
        } else {
          onError(error)
        }
      })
  }
}

// Helper method for non-jsonapi requests. It automatically handles cases like
// 400, 401, and 500 codes.
export const handleApiResponse = response => {
  if(response.status === 401) {
    return { unauthorized: <AuthenticationNotice/> }
  } else if(response.status === 403) {
    return { forbidden: 'Sorry, it looks like you don\'t have permission to do that.' }
  } else if(response.status === 500) {
    throw new Error('Server/Network Error')
  } else if(response.status === 204) {
    return {}
  } else {
    return response.json()
  }
}

export const findById = (arr, id) => {
  if(_.isEmpty(arr) || _.isNil(id)){ return {} }
  let foundObj = {}

  _.forEach(arr, obj => {
    if(obj && obj.id === id){ foundObj = obj }
  })
  return foundObj
}

// Returns an array of error messages from a graphql json response.
// This is intended to help devs pull errors out of a response without
// needing to know the exact structure of the error response.
export const responseErrors = json => {
  if (json.errors) {
    return json.errors.map(e => e.message)
  } else if (json.error) {
    return [json.error.message]
  } else {
    // Most graphql errors will return errors in the form { data: { myQueryOrMutationName: { errors: [...] } } }
    // so it's necessary to check all keys inside of 'data', ie if multiple queries/mutations were run in a single
    // request, there's going to be more than one key.

    let aggregateErrors = []
    _.each(Object.keys(json.data || {}), key => {
      aggregateErrors.push((json.data[key] || {}).errors)
    })

    return _.compact(_.flatten(aggregateErrors))
  }
}
